// components
import {
  Box,
  CircularProgress,
  GlobalStyles,
  MenuItem,
  Select,
  ThemeProvider,
  Typography,
} from '@mui/material';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { AiFillWarning } from 'react-icons/ai';
import { useSelector } from 'react-redux';

import useService from '@/lib/hooks/useService';
import { RootState } from '@/redux/store';
// utils
import theme from '@/styles/Theme/mui';
// types
import type Announcement from '@/types/database/announcement';

import AnnounceListItem from './AnnounceListItem';
import AnnouncementDetail from './AnnouncementDetail';
import { AnnouncementList, Container, globalStyles } from './styles';

const inputGlobalStyles = <GlobalStyles styles={globalStyles} />;

const now = moment().valueOf();

const AnnouncementFrontDesk: React.FC = () => {
  const user = useSelector((state: RootState) => state.user);
  const [categories] = useService({
    collectionName: 'categoryMenu',
    query: { service: 'announcement' },
  });
  const [page, setPage] = useState<'list' | 'detail'>('list');
  const [selectedAnnouncementType, setSelectedAnnouncementType] = useState<
    Announcement['type'] | '全部'
  >('全部');
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<Announcement | null>(null);

  const [announcements, , , loading] = useService<Announcement>({
    collectionName: 'announcement',
    query: {
      $or: [{ endTime: { $gt: now } }, { endTime: { $eq: null } }],
      $sort: {
        checkedStatus: -1,
        announcementTime: -1,
        type: 1,
      },
    },
  });

  const filteredAnnouncements = useMemo(
    () =>
      announcements.filter((announcement) => {
        if (selectedAnnouncementType === '全部') {
          return true;
        }
        return announcement.type === selectedAnnouncementType;
      }),
    [announcements, selectedAnnouncementType]
  );

  return (
    <>
      {inputGlobalStyles}
      <ThemeProvider theme={theme}>
        <Container>
          {page === 'list' && (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '& > *:not(:last-of-type)': {
                  marginBottom: '1rem',
                },
              }}
            >
              <Select
                sx={{
                  width: '100%',
                }}
                value={selectedAnnouncementType}
                onChange={(e) => {
                  setSelectedAnnouncementType(
                    e.target.value as typeof selectedAnnouncementType
                  );
                }}
              >
                <MenuItem value={'全部'}>全部</MenuItem>
                {categories[0] &&
                  categories[0].data.map(
                    (category: { id: string; name: string; color: string }) => {
                      return (
                        <MenuItem key={category.id} value={category.name}>
                          {category.name}
                        </MenuItem>
                      );
                    }
                  )}
              </Select>
              {!loading && filteredAnnouncements.length > 0 && (
                <AnnouncementList>
                  {filteredAnnouncements.map((announcement) => (
                    <AnnounceListItem
                      key={announcement._id}
                      item={announcement}
                      labelElement={announcement.type}
                      categoryMenu={categories[0].data}
                      onClick={() => {
                        setSelectedAnnouncement(announcement);
                        setPage('detail');
                      }}
                    />
                  ))}
                </AnnouncementList>
              )}
              {filteredAnnouncements.length === 0 && (
                <Box
                  id="info-box"
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  {loading && (
                    <>
                      <CircularProgress
                        size="5rem"
                        sx={{
                          marginBottom: '1rem',
                        }}
                      />
                      <Typography>載入中...</Typography>
                    </>
                  )}
                  {!loading && filteredAnnouncements.length === 0 && (
                    <>
                      <AiFillWarning color="rgb(238,167,33)" size="5rem" />
                      <Typography
                        sx={{
                          textAlign: 'center',
                        }}
                      >
                        {`目前沒有${
                          selectedAnnouncementType === '全部'
                            ? ''
                            : selectedAnnouncementType
                        }公告`}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}
          {page === 'detail' && selectedAnnouncement && (
            <AnnouncementDetail
              item={selectedAnnouncement}
              categories={categories[0].data}
              showDownloadBtn={user.auth.user?.keyboardAccount ? false : true}
              onClose={() => {
                setPage('list');
                setSelectedAnnouncement(null);
              }}
            />
          )}
        </Container>
      </ThemeProvider>
    </>
  );
};

export default AnnouncementFrontDesk;
